import { MutationFunctionOptions } from '@apollo/client';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCampaignContext } from '../context';
import {
  UpdateCampaignResponse,
  UpdateCampaignVariables,
  useUpdateCampaignMutation,
  getUpdateCampaignMutationPayload,
} from './useUpdateCampaignMutation';

export const useToggleAlerts = (
  options: Omit<
    MutationFunctionOptions<UpdateCampaignResponse, UpdateCampaignVariables>,
    'variables'
  > = {},
) => {
  const { campaign, refetch } = useCampaignContext();
  const { updateCampaign, data, error, isLoading } = useUpdateCampaignMutation();

  const dispatch = useDispatch();

  const toggleAlerts = useCallback(
    (campaignAlertsEnabled: boolean) => {
      const variables = getUpdateCampaignMutationPayload(campaign.id, {
        campaignAlertsEnabled,
      });

      updateCampaign({
        ...options,
        variables,
        onCompleted: (_data, clientOptions) => {
          const action = campaignAlertsEnabled ? 'enabled' : 'disabled';
          dispatch(addPositiveToast(`Alerts for this campaign have been ${action}.`));

          /**
           * TODO: instead of refetching here, we should use the data returned to `onCompleted` from the BE to update the cache
           * @see https://tanstack.com/query/v4/docs/react/guides/updates-from-mutation-responses
           */
          refetch();

          options?.onCompleted?.(_data, clientOptions);
        },
        onError: (e, clientOptions) => {
          const action = campaignAlertsEnabled ? 'enabling' : 'disabling';
          const errorMessage = `Error ${action} alerts for this campaign.`;
          dispatch(addNegativeToastWithErrorId(errorMessage, 'useToggleAlerts', error));

          options?.onError?.(e, clientOptions);
        },
      });
    },
    [updateCampaign, campaign.id],
  );

  return {
    data,
    error,
    isLoading,
    toggleAlerts,
  };
};
